import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { InferType } from 'yup';
import { mixed, object, string } from 'yup';

import { useDebounce } from '@/hooks/useDebounce';
import { useSettings } from '@/hooks/useSettings';
import { queries } from '@/queries';
import { type Settings, WorkspaceOptions } from '@/services/settings';
import { parseEnumToOptions } from '@/utils/parseEnumToOptions';

import TextInputField from '../Form/Fields/TextInputField';
import { Select } from '../Select';

type Props = {
  data?: Settings;
};

const Others: FC<Props> = ({ data }) => {
  const queryClient = useQueryClient();
  const { mutate } = useSettings({
    onSuccess: () => {
      queryClient.invalidateQueries(queries.externalCandidates.list._def);
    },
  });

  const { control, watch, register } = useForm<InferType<typeof schema>>({
    defaultValues: {
      workspaceType: data?.workspaceType ?? null,
      recruiterCity: data?.recruiterCity ?? '',
      recruiterName: data?.recruiterName ?? '',
    },
  });

  const debounced = useDebounce(watch());

  useEffect(() => {
    if (
      JSON.stringify([
        data?.workspaceType,
        data?.recruiterCity,
        data?.recruiterName,
      ]) ===
      JSON.stringify([
        watch('workspaceType'),
        debounced.recruiterCity,
        debounced.recruiterName,
      ])
    )
      return;
    mutate(watch());
  }, [JSON.stringify(watch()), debounced]);

  const optionsWorkspaceType = parseEnumToOptions(WorkspaceOptions);
  return (
    <form className="flex max-w-xs flex-col gap-4">
      <Controller
        control={control}
        name="workspaceType"
        render={({ field: { name, onChange, value, onBlur } }) => (
          <Select
            name={name}
            label="Type of workspace"
            variant="light"
            sizeVariant="sm"
            onChange={(newValue) => onChange(newValue?.value)}
            value={optionsWorkspaceType.find(
              (option) => option.value === value
            )}
            onBlur={onBlur}
            options={optionsWorkspaceType}
          />
        )}
      />
      <TextInputField
        {...register('recruiterName')}
        variant="light"
        className="border-neutral-1000"
        sizeVariant="xs"
        label="Recruiter Name"
        containerClassName="text-neutral-1000 !gap-2"
        labelClassName="text-xs"
      />
      <TextInputField
        {...register('recruiterCity')}
        variant="light"
        className="border-neutral-1000"
        sizeVariant="xs"
        label="Recruiter City"
        containerClassName="text-neutral-1000 !gap-2"
        labelClassName="text-xs"
      />
    </form>
  );
};

export default Others;

export const schema = object({
  workspaceType: mixed<WorkspaceOptions | ''>()
    .nullable()
    .defined()
    .oneOf([...Object.values(WorkspaceOptions), null, '']),
  recruiterName: string(),
  recruiterCity: string(),
});
